/** 預設空函式，用於判斷 register / onChange 是否由呼叫端提供 */
export function noop() {}

/**
 * 從嵌套路徑取得 react-hook-form 錯誤（如 add_warehouse.0.uuid）
 * @param {object|null|undefined} errors
 * @param {string|undefined} path
 * @returns {object|null}
 */
export function getNestedError(errors, path) {
  if (!errors || !path) {
    return null;
  }

  if (errors[path]) {
    return errors[path];
  }

  const parts = path.split('.');
  let current = errors;

  for (const part of parts) {
    if (current == null || typeof current !== 'object') {
      return null;
    }

    if (!Number.isNaN(Number(part)) && Array.isArray(current)) {
      current = current[Number(part)];
    } else {
      current = current[part];
    }

    if (current == null) {
      return null;
    }
  }

  return current;
}

/**
 * @param {Function|typeof noop|undefined} register
 * @returns {boolean}
 */
export function hasRegisterFn(register) {
  return typeof register === 'function' && register !== noop;
}

/**
 * 遞迴收集 RHF errors 葉節點 message，供 FormValidationSummary 總提示使用。
 * @param {object|null|undefined} errors
 * @param {object} [options]
 * @param {string[]} [options.excludeFields]
 * @param {Record<string, string>} [options.fieldLabels]
 * @param {(path: string) => string|undefined} [options.fieldLabelResolver]
 * @returns {{ key: string, text: string }[]}
 */
export function collectFormValidationErrors(errors, options = {}) {
  const {
    excludeFields = [],
    fieldLabels = {},
    fieldLabelResolver,
  } = options;

  const collected = [];
  const seenKeys = new Set();

  function isExcluded(path) {
    if (!path) {
      return false;
    }

    const topKey = path.split('.')[0];
    return excludeFields.includes(path) || excludeFields.includes(topKey);
  }

  function resolveLabel(path) {
    if (fieldLabelResolver) {
      const resolved = fieldLabelResolver(path);
      if (resolved) {
        return resolved;
      }
    }

    if (fieldLabels[path]) {
      return fieldLabels[path];
    }

    const topKey = path.split('.')[0];
    if (fieldLabels[topKey]) {
      return fieldLabels[topKey];
    }

    return null;
  }

  function formatText(path, message) {
    const label = resolveLabel(path);
    return label ? `${label}：${message}` : message;
  }

  function pushError(path, message) {
    if (!message || isExcluded(path)) {
      return;
    }

    const key = path || 'form';
    if (seenKeys.has(key)) {
      return;
    }

    seenKeys.add(key);
    collected.push({
      key,
      text: formatText(path, message),
    });
  }

  function walk(node, path) {
    if (node == null || typeof node !== 'object') {
      return;
    }

    if (typeof node.message === 'string' && node.message) {
      pushError(path, node.message);
    }

    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        const childPath = path ? `${path}.${index}` : String(index);
        walk(item, childPath);
      });
      return;
    }

    Object.keys(node).forEach((key) => {
      if (key === 'ref' || key === 'type' || key === 'types' || key === 'message') {
        return;
      }

      const childPath = path ? `${path}.${key}` : key;
      walk(node[key], childPath);
    });
  }

  walk(errors, '');
  return collected;
}
